import { Component, Prop } from 'vue-property-decorator'
import { mixins } from 'vue-class-component'
import { Pawn, Player } from '@/services/store/types'
import { playerColors } from '@/constants/player'
import MixinStock from '@/mixins/stock'
import MixinPlayer from '@/mixins/player'
import Manager, { vageDragEvents, VageDragEvent } from '@/plugins/drag/manager'
import ModelMolecule from '@/models/molecule'
import ModelConnection from '@/models/connection'
import { moleculeTypes, moleculeCategories, visibleChainLengthMolecules } from '@/constants/molecule'
import { localEvents } from '@/constants/events'

@Component
export default class MoleculePawn extends mixins(MixinStock, MixinPlayer) {
  @Prop() public pawn: Pawn

  private dragManager: Manager
  private isDraggedOverCurrentMolecule: boolean = false

  get player(): Player | undefined {
    return this.players.find((player: Player) => player.id === this.pawn.playerID)
  }

  get className(): string {
    if (this.player) {
      return playerColors[this.player.teamID][this.player.type]
    }

    return 'free'
  }

  get molecule() {
    return this.$molecules.find(this.pawn.moleculeID) as ModelMolecule
  }

  get isChainLengthVisible() {
    return visibleChainLengthMolecules.includes(this.pawn.moleculeID) &&
      this.pawn.moleculeID !== 'j2971q94' // tag
  }

  get chainLength() {
    return this.pawn.chainLengths[0]
  }

  get teamPlayerIDs(): string[] {
    return this.players
      .filter((player: Player) => player.teamID === this.activePlayer.teamID)
      .map((player: Player) => player.id)
  }

  get isPawnDraggable(): boolean {
    if (this.pawn.playerID !== null) {
      if (!this.teamPlayerIDs.includes(this.pawn.playerID as string)) {
        return false
      }
    }

    return true
  }

  public mounted() {
    if (!this.isPawnDraggable) {
      return
    }

    this.dragManager = this.$drag.add(this.$el as HTMLElement, {})
    this.dragManager.on(vageDragEvents.DROP, this.dropListener)
    this.dragManager.on(vageDragEvents.DRAG, this.dragListener)
  }

  private dragListener(event: VageDragEvent) {
    const moleculeElement: HTMLElement | null = this.getMolecule(event)

    if (moleculeElement) {
      this.$emit(localEvents.PAWN_DRAG_OVER_MOLECULE, {
        chainLength: this.pawn.chainLengths[0],
        doubleBinding: this.pawn.doubleBindings[0],
      })
    }
  }

  private dropListener(event: VageDragEvent) {
    this.$emit(localEvents.PAWN_DRAG_STOP)
    this.$emit(localEvents.PAWN_DRAG_OUT_MOLECULE)

    if (!this.isPawnDraggable) {
      return
    }

    const moleculeElement: HTMLElement | null = this.getMolecule(event)

    if (!moleculeElement) {
      return
    }

    const currentMolecule: ModelMolecule = this.$molecules.find(this.pawn.moleculeID as string)
    const targetMolecule: ModelMolecule = this.$molecules.find(moleculeElement.dataset.id as string)

    if (!currentMolecule.hasConnectionTo(targetMolecule)) {
      return
    }

    const connection: ModelConnection = currentMolecule.getConnectionTo(targetMolecule)

    if (connection.isInvalid(this.pawn)) {
      return
    }

    if (!this.stockHasEnzymesForConnection(connection)) {
      return
    }

    if (!this.playerHasPawnsForConnection(connection)) {
      return
    }

    if (targetMolecule.id === 'j2971q94') {
      return this.$modal.open('create-tag')
    }

    this.stockProcessConnectionUsage(connection)
    this.playerProcessConnectionUsage(connection, this.pawn)

    if (targetMolecule.type === moleculeTypes.PRODUCT && targetMolecule.shouldCreateProduct(currentMolecule)) {
      this.stockCreateProduct(targetMolecule, this.pawn)
      this.storeUpdatePawnAmount({
        player: this.activePlayer,
        amount: 1,
      })
    }

    this.checkGameWonByTeam()
  }

  private getMolecule(event: VageDragEvent): HTMLElement | null {
    const { x, y } = event
    const element: Element | null = document.elementFromPoint(x, y)

    if (element) {
      return element.closest('.molecule') as HTMLElement
    }

    return null
  }
}
